import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MicIcon from "@mui/icons-material/Mic";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useHistory, useLocation } from "react-router-dom";
import dayjs from "dayjs";

import { Colors } from "../../../Styles/Colors";
import RestAPIService from "../../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../../Utility/API/Serviceurls";
import ToastMsg from "../../../Components/ToastMsg/ToastMsg";
import Loader from "../../../Components/Loader";
import { ImagePaths } from "../../../Utility/ImagePaths";
import VoiceToText from "../../../Components/Common Components/VoiceToText";

export default function TherapistSession() {
    const history = useHistory();
    const location = useLocation();
    const walkinId = location.state?.id;

    const [treatmentData, setTreatmentData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState(false);
    const [errorMsgText, setErrorMsgText] = useState("");
    const [successMsg, setSuccessMsg] = useState(false);
    const [successMsgText, setSuccessMsgText] = useState("");
    const [checklistOpen, setChecklistOpen] = useState(false);
    const [checklistData, setChecklistData] = useState({ medications: [], Equipments: [], Materials: [] });
    const [checklistChecked, setChecklistChecked] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [therapistNotes, setTherapistNotes] = useState("");
    const [sessionStarted, setSessionStarted] = useState(false);
    const [sessionStartedAt, setSessionStartedAt] = useState(null);
    const [pausedElapsedSeconds, setPausedElapsedSeconds] = useState(0);
    const [timerTick, setTimerTick] = useState(0);
    const [isCancelled, setIsCancelled] = useState(false);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [cancelReason, setCancelReason] = useState("");
    const [listenPopUp, setListenPopUp] = useState(false);
    const [timeoutAlarmOpen, setTimeoutAlarmOpen] = useState(false);
    const alarmTriggeredRef = useRef(false);
    const alarmBeepIntervalRef = useRef(null);

    const row = location.state?.row || {};
    const startedTimeFromHistory = location.state?.started_time ?? row?.started_time;
    const stoppedTimeFromHistory = location.state?.stopped_time ?? row?.stopped_time;
    const statusFromHistory = (location.state?.status ?? row?.status ?? "").toString().toLowerCase();
    const patientName = row.patient_name || treatmentData?.patient_name || "-";
    const patientId = row.patient_account_number || treatmentData?.patient_account_number || "-";
    const patientPhone = row.patient_mobile_number || row.patient_mobile_number || "-";

    useEffect(() => {
        if (walkinId) {
            fetchTreatment();
        } else {
            setErrorMsgText("Invalid appointment");
            setErrorMsg(true);
            setLoading(false);
        }
    }, [walkinId]);

    useEffect(() => {
        const startedAtRaw = startedTimeFromHistory ?? treatmentData?.session_started_at ?? treatmentData?.started_at ?? treatmentData?.start_time;
        const startedParsed = startedAtRaw && dayjs(startedAtRaw).isValid() ? dayjs(startedAtRaw) : null;

        const stoppedArr = stoppedTimeFromHistory ?? treatmentData?.stopped_time;
        if (stoppedArr != null && startedParsed) {
            let stoppedAtStr = null;
            if (Array.isArray(stoppedArr) && stoppedArr.length > 0) {
                const lastEntry = stoppedArr[stoppedArr.length - 1];
                stoppedAtStr = lastEntry?.stopped_time ?? null;
            } else if (typeof stoppedArr === "string") {
                stoppedAtStr = stoppedArr;
            }
            if (stoppedAtStr) {
                const stopped = dayjs(stoppedAtStr);
                if (stopped.isValid()) {
                    setSessionStartedAt(startedParsed);
                    setSessionStarted(false);
                    const elapsed = Math.max(0, Math.floor(stopped.diff(startedParsed, "second")));
                    setPausedElapsedSeconds(elapsed);
                    return;
                }
            }
        }

        const status = (treatmentData?.status || "").toLowerCase();
        const isStarted = status === "started" || status === "in progress" || status === "inprogress";
        if (startedParsed && (isStarted || startedTimeFromHistory)) {
            setSessionStartedAt(startedParsed);
            setSessionStarted(true);
        }
    }, [treatmentData, startedTimeFromHistory, stoppedTimeFromHistory]);

    useEffect(() => {
        if (!sessionStarted) {
            alarmTriggeredRef.current = false;
        }
    }, [sessionStarted]);

    useEffect(() => {
        if (!sessionStarted) return;
        const interval = setInterval(() => setTimerTick((t) => t + 1), 1000);
        return () => clearInterval(interval);
    }, [sessionStarted]);

    const getTotalDurationSeconds = () => {
        const treatments = treatmentData?.treatments || [];
        let totalSeconds = 0;
        treatments.forEach((t) => {
            const d = Number(t.duration);
            if (Number.isNaN(d)) return;
            const mode = (t.duration_mode || "Minutes").toLowerCase();
            if (mode === "hours") totalSeconds += d * 3600;
            else totalSeconds += d * 60;
        });
        return totalSeconds || 0;
    };

    const totalDurationSeconds = getTotalDurationSeconds();
    const now = dayjs();
    const startedAt = sessionStartedAt ? dayjs(sessionStartedAt) : null;
    const elapsedSeconds = sessionStarted && startedAt
        ? Math.max(0, Math.floor(now.diff(startedAt, "second")))
        : pausedElapsedSeconds;
    const isOverDuration = totalDurationSeconds > 0 && elapsedSeconds >= totalDurationSeconds;

    // Trigger timeout alarm once when therapy duration is exceeded (session still running)
    useEffect(() => {
        if (sessionStarted && isOverDuration && totalDurationSeconds > 0 && !alarmTriggeredRef.current) {
            alarmTriggeredRef.current = true;
            setTimeoutAlarmOpen(true);
        }
    }, [sessionStarted, isOverDuration, totalDurationSeconds]);

    // Play repeating beep while timeout alarm dialog is open; stop on close
    useEffect(() => {
        if (!timeoutAlarmOpen) {
            if (alarmBeepIntervalRef.current) {
                clearInterval(alarmBeepIntervalRef.current);
                alarmBeepIntervalRef.current = null;
            }
            return;
        }
        let audioContext = null;
        const playBeep = () => {
            try {
                if (!audioContext) {
                    audioContext = new (window.AudioContext || window.webkitAudioContext)();
                }
                const oscillator = audioContext.createOscillator();
                const gainNode = audioContext.createGain();
                oscillator.connect(gainNode);
                gainNode.connect(audioContext.destination);
                oscillator.frequency.value = 800;
                oscillator.type = "sine";
                gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
                gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
                oscillator.start(audioContext.currentTime);
                oscillator.stop(audioContext.currentTime + 0.3);
            } catch (e) {
                console.warn("Alarm beep not supported", e);
            }
        };
        playBeep();
        alarmBeepIntervalRef.current = setInterval(playBeep, 1200);
        return () => {
            if (alarmBeepIntervalRef.current) {
                clearInterval(alarmBeepIntervalRef.current);
                alarmBeepIntervalRef.current = null;
            }
            if (audioContext) {
                audioContext.close().catch(() => {});
            }
        };
    }, [timeoutAlarmOpen]);

    useEffect(() => {
        fetchChecklist();
    }, []);

    const formatTimer = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    };

    const formatTreatmentDuration = (treatment) => {
        const d = Number(treatment?.duration);
        if (d == null || Number.isNaN(d)) return "-";
        const mode = (treatment?.duration_mode || "Minutes").toString().toLowerCase();
        if (mode === "hours") return `${d} Hour${d !== 1 ? "s" : ""}`;
        return `${d} Min`;
    };

    const fetchTreatment = () => {
        setLoading(true);
        RestAPIService.getAll(Serviceurls.AYURVEDA_MEDICINE_PREPARATION_TREATMENT + "?id=" + walkinId)
            .then((res) => {
                if (res?.data?.status === "success" && res?.data?.data) {
                    setTreatmentData(res.data.data);
                } else {
                    setErrorMsgText(res?.data?.message || "Failed to load");
                    setErrorMsg(true);
                }
            })
            .catch((e) => {
                setErrorMsgText(e?.response?.data?.message || e.message || "Failed to load");
                setErrorMsg(true);
            })
            .finally(() => setLoading(false));
    };

    const fetchTherapistGet = () => {
        const appointmentDate = location.state?.APIDate ? dayjs(location.state?.APIDate).format("YYYY-MM-DD") : "";
        RestAPIService.getAll(Serviceurls.AYURVEDA_THERAPIST_GET + `?appointment_date=${appointmentDate}&status=${location.state?.APIStatus}`)
            .then((res) => {
                const list = res?.data?.data ?? res?.data ?? [];
                const arr = Array.isArray(list) ? list : [];
                const match = arr.find((item) => Number(item.id) === Number(walkinId) || item.id === walkinId);
                if (!match) return;
                const stoppedTimeArr = match.stopped_time;
                if (!Array.isArray(stoppedTimeArr) || stoppedTimeArr.length === 0) return;
                const lastEntry = stoppedTimeArr[stoppedTimeArr.length - 1]; // <-- fixed
                const stoppedAt = lastEntry?.stopped_time;
                if (!stoppedAt) return;
                const started = sessionStartedAt ? dayjs(sessionStartedAt) : (match.started_time ? dayjs(match.started_time) : null);
                if (!started || !started.isValid()) return;
                const stopped = dayjs(stoppedAt);
                if (!stopped.isValid()) return;
                const elapsed = Math.max(0, Math.floor(stopped.diff(started, "second")));
                setPausedElapsedSeconds(elapsed);
                setSessionStarted(false);
            })
            .catch(() => {});
    };

    const fetchChecklist = () => {
        RestAPIService.getAll(Serviceurls.AYURVEDA_THERAPY_CHECKLIST + "?id=" + walkinId)
            .then((res) => {
                if (res?.data?.status === "success" && res?.data?.data) {
                    const data = res.data.data;
                    setChecklistData({
                        Materials: data.Materials || [],
                        Equipments: data.Equipments || [],
                    });
                    const checked = {};
                    (data.Equipments || []).forEach((e) => { checked[`eq-${e.id}`] = true; });
                    (data.Materials || []).forEach((m) => { checked[`met-${m.id}`] = true; });
                    setChecklistChecked(checked);
                    // Only auto-open checklist when status from history is pending or in progress
                    const shouldOpenChecklist = statusFromHistory === "medication ready" || statusFromHistory === "in progress" || statusFromHistory === "inprogress";
                    if (shouldOpenChecklist) setChecklistOpen(true);
                } else {
                    setErrorMsgText(res?.data?.message || "Failed to load checklist");
                    setErrorMsg(true);
                }
            })
            .catch((e) => {
                setErrorMsgText(e?.response?.data?.message || e.message || "Failed to load checklist");
                setErrorMsg(true);
            });
    };

    const PostCardDetails = (status) => {
        try {
            setSubmitting(true);
            const data = { id: walkinId, status };
            RestAPIService.create(data, Serviceurls.AYURVEDA_THERAPIST_POST)
                .then((response) => {
                    if (response.data.status === "success") {
                        setSuccessMsgText(response?.data?.message || "Status Updated Successfully");
                        setSuccessMsg(true);
                        if (status === "Started") {
                            setSessionStartedAt(dayjs());
                            setSessionStarted(true);
                            setPausedElapsedSeconds(0);
                        } else if (status === "Stopped") {
                            // stop timer immediately in UI
                            setSessionStarted(false);

                            // calculate elapsed time (use known start time if available)
                            const started = sessionStartedAt ? dayjs(sessionStartedAt) : (treatmentData?.started_at ? dayjs(treatmentData.started_at) : null);
                            if (started && started.isValid()) {
                                const elapsed = Math.max(0, Math.floor(dayjs().diff(started, "second")));
                                setPausedElapsedSeconds(elapsed);
                            }

                            fetchTherapistGet(); // keep this to refresh the backend state if needed
                        }
                        fetchTreatment();
                    } else {
                        setErrorMsgText(response?.data?.message || "Update failed");
                        setErrorMsg(true);
                    }
                })
                .catch((error) => {
                    setErrorMsgText(error?.response?.data?.message || error?.message || "Update failed");
                    setErrorMsg(true);
                })
                .finally(() => setSubmitting(false));
        } catch (e) {
            setErrorMsgText(e?.response?.data?.message || e?.message || "Update failed");
            setErrorMsg(true);
            setSubmitting(false);
        }
    };

    const handleStatusUpdate = (payload) => {
        setSubmitting(true);
        RestAPIService.updateWithOutId(payload, Serviceurls.AYURVEDA_WALKIN_APPOINTMENT_STATUS_UPDATE)
            .then((res) => {
                if (res?.data?.status === "success") {
                    setSuccessMsgText(res?.data?.message || "Updated");
                    setSuccessMsg(true);
                    fetchTreatment();
                } else {
                    setErrorMsgText(res?.data?.message || "Update failed");
                    setErrorMsg(true);
                }
            })
            .catch((e) => {
                setErrorMsgText(e?.response?.data?.message || e.message || "Update failed");
                setErrorMsg(true);
            })
            .finally(() => setSubmitting(false));
    };


    const handleStart = (treatment) => {
        const payload = {
            walkin_appointment_id: walkinId,
            treatment_drug_id: treatment.drugs?.[0]?.id,
            status: "started",
        };
        handleStatusUpdate(payload);
        setTreatmentStates((prev) => ({
            ...prev,
            [treatment.id]: { ...prev[treatment.id], status: "Started", start_time: dayjs().format("HH:mm:ss") },
        }));
    };

    const handleStop = (treatment) => {
        const payload = { id: treatment.id, status: "stopped" };
        handleStatusUpdate(payload);
        setTreatmentStates((prev) => ({
            ...prev,
            [treatment.id]: { ...prev[treatment.id], status: "Stopped" },
        }));
    };

    const handleComplete = (treatment) => {
        const payload = { id: treatment.id, status: "Completed" };
        handleStatusUpdate(payload);
        setTreatmentStates((prev) => ({
            ...prev,
            [treatment.id]: { ...prev[treatment.id], status: "Completed", end_time: dayjs().format("HH:mm:ss") },
        }));
    };

    const handleCancelSession = () => {
        setSubmitting(true);
        const payload = { id: walkinId, cancelled_reason: cancelReason || "" };
        RestAPIService.create(payload, Serviceurls.AYURVEDA_WALKIN_APPOINTMENT_LIST)
            .then((response) => {
                if (response?.data?.status === "success") {
                    setIsCancelled(true);
                    setCancelDialogOpen(false);
                    setCancelReason("");
                    setSuccessMsgText(response?.data?.message || "Session cancelled");
                    setSuccessMsg(true);
                    setTimeout(() => {
                        history.push("/AyurvedaTherapist");
                    }, 1000);
                } else {
                    setErrorMsgText(response?.data?.message || "Cancel failed");
                    setErrorMsg(true);
                }
            })
            .catch((error) => {
                setErrorMsgText(error?.response?.data?.message || error?.message || "Cancel failed");
                setErrorMsg(true);
            })
            .finally(() => setSubmitting(false));
    };

    const handleCompleteSession = () => {
        setSubmitting(true);
        const payload = {
            id: walkinId,
            status: "Completed",
            therapist_notes: therapistNotes || "",
        };
        RestAPIService.create(payload, Serviceurls.AYURVEDA_WALKIN_APPOINTMENT_STATUS_UPDATE)
            .then((res) => {
                if (res?.data?.status === "success") {
                    setSuccessMsgText(res?.data?.message || "Completed");
                    setSuccessMsg(true);
                    setTimeout(() => {
                        history.push("/AyurvedaTherapist");
                    }, 2000);
                } else {
                    setErrorMsgText(res?.data?.message || "Complete failed");
                    setErrorMsg(true);
                }
            })
            .catch((e) => {
                setErrorMsgText(e?.response?.data?.message || e.message || "Complete failed");
                setErrorMsg(true);
            })
            .finally(() => setSubmitting(false));
    };

    const voiceToTextgetter = (textStorage) => {
        if (textStorage && textStorage.trim() !== "") {
            setTherapistNotes((prev) => (prev ? prev + " " + textStorage.trim() : textStorage.trim()));
        }
        setListenPopUp(false);
    };

    const listenPopUpClose = () => {
        setListenPopUp(false);
    };

    const canCompleteSession = !sessionStarted && !!sessionStartedAt && !isCancelled && row.status !== "Completed";

    const goBack = () => history.push("/AyurvedaTherapist");

    if (loading && !treatmentData) {
        return <Loader />;
    }

    const treatments = treatmentData?.treatments || [];

    return (
        <Box sx={{ height: "85vh", width: "100%", backgroundColor: Colors.Background, p: 1, overflow: "auto" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", backgroundColor: "white", p: 1, borderBottom: "1px solid #eee" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <IconButton size="small" onClick={goBack} sx={{ color: "#4da3d4" }}>
                        <ChevronLeftIcon />
                    </IconButton>
                    <Typography color="textSecondary" fontWeight={600} fontSize="0.95vw">
                        Therapist Home
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", backgroundColor: "white", p: 1.5, mt: 1, flexWrap: "wrap", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: "50%", overflow: "hidden", bgcolor: "#e0e0e0" }}>
                        <img src={row.photo_url || ImagePaths.Male.default} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </Box>
                    <Box>
                        <Typography fontSize="0.9vw" fontWeight={500}>{patientName} {row.patient_age ? `${row.patient_age}/y` : ""}</Typography>
                        <Typography fontSize="0.75vw" color="textSecondary">{patientId} | {patientPhone}</Typography>
                    </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Button
                        variant="outlined"
                        disabled={submitting || isCancelled || row.status === "Completed"}
                        startIcon={<CheckCircleIcon />}
                        color="error"
                        onClick={() => setCancelDialogOpen(true)}
                        sx={{ textTransform: "uppercase", fontSize: "0.8vw" }}
                    >
                        Cancel Session
                    </Button>
                    {sessionStarted ? (
                        <Button
                            variant="contained"
                            disabled={submitting || isCancelled}
                            onClick={() => PostCardDetails("Stopped")}
                            startIcon={<StopIcon />}
                            sx={{
                                bgcolor: "#04B7B1",
                                "&:hover": { bgcolor: "#04B7B1" },
                                textTransform: "uppercase",
                                fontSize: "0.8vw",
                            }}
                        >
                            Stop Session
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            disabled={submitting || isCancelled || !!sessionStartedAt || row.status === "Completed"}
                            onClick={() => PostCardDetails("Started")}
                            startIcon={<PlayArrowIcon />}
                            sx={{
                                bgcolor: "#04B7B1",
                                "&:hover": { bgcolor: "#04B7B1" },
                                textTransform: "uppercase",
                                fontSize: "0.8vw",
                            }}
                        >
                            Start Session
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        disabled={submitting || !canCompleteSession || isCancelled || row.status === "Completed"}
                        onClick={handleCompleteSession}
                        startIcon={<CheckCircleIcon />}
                        sx={{
                            bgcolor: canCompleteSession ? "#2e7d32" : "#9e9e9e",
                            "&:hover": { bgcolor: canCompleteSession ? "#1b5e20" : "#757575" },
                            textTransform: "uppercase",
                            fontSize: "0.8vw",
                        }}
                    >
                        Complete Session
                    </Button>
                    <Box
                        key={sessionStarted ? timerTick : "paused"}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            px: 1.5,
                            py: 0.75,
                            border: "2px solid",
                            borderColor: isOverDuration ? "#B8170D" : "#B87333",
                            borderRadius: 1,
                            backgroundColor: isOverDuration ? "#ffebee" : "#fff8e1",
                        }}
                    >
                        <AccessTimeIcon sx={{ fontSize: "1.1vw", color: isOverDuration ? "#B8170D" : "#B87333" }} />
                        <Typography
                            sx={{
                                fontSize: "0.95vw",
                                fontWeight: 600,
                                color: isOverDuration ? "#B8170D" : "#B87333",
                            }}
                        >
                            {formatTimer(elapsedSeconds)} / {formatTimer(totalDurationSeconds)}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ backgroundColor: "white", mt: 1, p: 1 }}>
                <Typography fontWeight={600} fontSize="0.9vw" sx={{ mb: 1 }}>Therapy</Typography>
                <TableContainer>
                    <Table size="small" sx={{ "& .MuiTableCell-root": { fontSize: "0.8vw", border: "1px solid #e0e0e0" } }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: Colors.Primary }}>
                                <TableCell sx={{ verticalAlign: "top", color: "white", fontWeight: 600 }}>Treatment Name</TableCell>
                                <TableCell sx={{ verticalAlign: "top", color: "white", fontWeight: 600 }}>Drug Name</TableCell>
                                <TableCell sx={{ verticalAlign: "top", color: "white", fontWeight: 600 }}>UOM</TableCell>
                                <TableCell sx={{ verticalAlign: "top", color: "white", fontWeight: 600 }}>Duration</TableCell>
                                <TableCell sx={{ verticalAlign: "top", color: "white", fontWeight: 600 }}>Instruction</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {treatments.map((treatment, tIdx) => {
                                const drugs = treatment.drugs || [];
                                return (
                                    <React.Fragment key={treatment.id}>
                                        {drugs.map((drug, dIdx) => {
                                            const drugGroupIndex = treatments
                                                .slice(0, tIdx)
                                                .reduce((acc, t) => acc + (t.drugs || []).length, 0) + dIdx;
                                            const borderColor = drugGroupIndex % 2 === 0 ? "gray" : "#4da3d4";
                                            const rowBg = drugGroupIndex % 2 === 0 ? "#fafafa" : "#f0f8ff";
                                            const rowSpan = drugs.reduce((acc, d) => acc + 1 + (d.combinations?.length || 0), 0);
                                            return (
                                                <React.Fragment key={drug.id}>
                                                    <TableRow sx={{ bgcolor: rowBg }}>
                                                        {dIdx === 0 ? (
                                                            <TableCell rowSpan={rowSpan} sx={{ verticalAlign: "top", pl: 1 }}>
                                                                <Typography fontWeight={500}>{treatment.treatment_name} -{String(tIdx + 1).padStart(2, "0")}</Typography>
                                                            </TableCell>
                                                        ) : null}
                                                        <TableCell sx={{ verticalAlign: "top", pl: 1, borderLeft: `5px solid ${borderColor}` }}>
                                                            <Typography fontSize="0.8vw">{drug.drug_name}</Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ verticalAlign: "top" }}>
                                                            <Typography fontSize="0.8vw">{drug.quantity} {drug.uom}</Typography>
                                                        </TableCell>
                                                        {dIdx === 0 ? (
                                                            <TableCell rowSpan={rowSpan} sx={{ verticalAlign: "top" }}>{formatTreatmentDuration(treatment)}</TableCell>
                                                        ) : null}
                                                        {dIdx === 0 ? (
                                                            <TableCell rowSpan={rowSpan} sx={{ verticalAlign: "top" }}>{treatmentData?.instruction || "-"}</TableCell>
                                                        ) : null}
                                                    </TableRow>
                                                    {(drug.combinations || []).map((comb) => (
                                                        <TableRow key={comb.id} sx={{ bgcolor: rowBg }}>
                                                            <TableCell sx={{ verticalAlign: "top", pl: 1.5, borderLeft: `5px solid ${borderColor}` }}>
                                                                <Box sx={{ borderLeft: "3px solid rgba(0,0,0,0.12)", pl: 1 }}>
                                                                    <Typography fontSize="0.8vw">{comb.drug_name}</Typography>
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell sx={{ verticalAlign: "top" }}>
                                                                <Typography fontSize="0.8vw">{comb.quantity} {comb.uom}</Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </React.Fragment>
                                            );
                                        })}
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <Box sx={{ backgroundColor: "white", mt: 1, p: 1 }}>
                <Typography fontWeight={600} fontSize="0.9vw" sx={{ mb: 0.5 }}>Therapist Notes</Typography>
                <Typography fontSize="0.75vw" color="textSecondary" sx={{ mb: 0.5 }}>Enter notes for this session (sent as therapist_notes on Complete Session)</Typography>
                <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={6}
                    value={therapistNotes}
                    onChange={(e) => setTherapistNotes(e.target.value)}
                    placeholder="e.g. Session notes, follow-up, patient feedback..."
                    variant="outlined"
                    size="small"
                    sx={{ "& .MuiInputBase-input": { fontSize: "0.85vw" } }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    onClick={() => setListenPopUp(true)}
                                    title="Voice to text"
                                    aria-label="Voice to text"
                                >
                                    <MicIcon sx={{ color: "grey.700", cursor: "pointer" }} />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <Dialog open={checklistOpen}  maxWidth="md" fullWidth>
                <DialogTitle>Therapy Checklist</DialogTitle>
                <DialogContent>
                    <Stack direction="row" spacing={4} sx={{ pt: 1 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography fontWeight={600} sx={{ mb: 1 }}>EQUIPMENTS</Typography>
                            {(checklistData?.Equipments || []).map((item) => (
                                <Box key={item.id} sx={{ display: "flex", alignItems: "center", py: 0.5 }}>
                                    <Checkbox
                                        checked={checklistChecked[`eq-${item.id}`] ?? true}
                                        onChange={(e) => setChecklistChecked((prev) => ({ ...prev, [`eq-${item.id}`]: e.target.checked }))}
                                        size="small"
                                    />
                                    <Typography fontSize="0.9vw">{item.name}</Typography>
                                </Box>
                            ))}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography fontWeight={600} sx={{ mb: 1 }}>MATERIALS</Typography>
                            {(checklistData?.Materials || []).map((item) => (
                                <Box key={item.id} sx={{ display: "flex", alignItems: "center", py: 0.5 }}>
                                    <Checkbox
                                        checked={checklistChecked[`med-${item.id}`] ?? true}
                                        onChange={(e) => setChecklistChecked((prev) => ({ ...prev, [`med-${item.id}`]: e.target.checked }))}
                                        size="small"
                                    />
                                    <Typography fontSize="0.9vw">{item.name}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => {
                        setChecklistOpen(false);
                        goBack();
                    }}>Cancel</Button>
                    <Button variant="contained" color="primary" onClick={() => setChecklistOpen(false)} disabled={submitting}>
                        Receive
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={cancelDialogOpen} onClose={() => !submitting && setCancelDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Cancel Session</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Reason for cancellation"
                        multiline
                        minRows={2}
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        placeholder="Enter reason"
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setCancelDialogOpen(false)} disabled={submitting}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleCancelSession} disabled={submitting}>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={timeoutAlarmOpen} onClose={() => {}} maxWidth="xs" fullWidth disableEscapeKeyDown>
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <WarningAmberIcon sx={{ color: "warning.main", fontSize: 28 }} />
                    Time's up for therapy
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Exceeding time. Please complete the session and stop when done.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setTimeoutAlarmOpen(false);
                        }}
                        sx={{ minWidth: 80 }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {errorMsg && <ToastMsg severity="error" msg={errorMsgText} msgPop={() => { setErrorMsg(false); setErrorMsgText(""); }} />}
            {successMsg && <ToastMsg severity="success" msg={successMsgText} msgPop={() => { setSuccessMsg(false); setSuccessMsgText(""); }} />}
            {submitting && <Loader />}
            {listenPopUp && (
                <VoiceToText
                    voiceTextConverter={voiceToTextgetter}
                    listenPopUp={listenPopUpClose}
                />
            )}
        </Box>
    );
}